import { Component } from 'react'
import PropTypes from 'prop-types'
import { reduxForm } from 'redux-form'

import { createValidator, required } from 'lib/form-validation'
import {
  getExpectingChildren,
  getHasPartner,
  getPartner,
  getPartnerCollaborator,
  getPlanningStage,
  getRegistryId,
  getUserOwnsRegistry,
} from 'registry/reducers'
import { saveRegistry } from 'registry/actions'

import { map } from 'lodash'
import { Col, Collapse, Row, ModalFooter } from 'react-bootstrap'
import { SaveButton } from 'components/buttons'
import Link from 'components/link'

import { Form, Radio, RadioGroup } from 'components/forms'
import AddOne from 'components/add-one'

import NameFieldset from 'registry/forms/name-fieldset'
import PartnerAccessForm from 'registry/forms/partner-access-form'

import { CONFIG } from 'shared/constants'

export class FamilyDetailsForm extends Component {
  static propTypes = {
    fields: PropTypes.shape({
      registry: PropTypes.shape({
        id: PropTypes.object.isRequired,
        family: PropTypes.shape({
          hasPartner: PropTypes.object.isRequired,
          partner: PropTypes.object.isRequired,
          planningStage: PropTypes.object.isRequired,
          expectingChildren: PropTypes.array.isRequired,
        }),
      }),
      user: PropTypes.shape({
        firstName: PropTypes.object.isRequired,
        lastName: PropTypes.object.isRequired,
      }),
    }),
    submitting: PropTypes.bool,
    userOwnsRegistry: PropTypes.bool,
    partnerCollaborator: PropTypes.object,
  }

  render() {
    let {
      fields: {
        registry: { family },
        user,
      },
    } = this.props
    let partnerInvited = !!this.props.partnerCollaborator
    return (
      <Form
        error={this.props.error}
        onSubmit={this.props.handleSubmit(this.handleSubmit.bind(this))}
      >
        {this.props.userOwnsRegistry ? (
          <div>
            {!partnerInvited ? (
              <RadioGroup
                title="Do you have a partner on this adventure?"
                horizontal
              >
                <Radio
                  {...family.hasPartner}
                  label="Yes"
                  checked={family.hasPartner.value === 'true'}
                  value="true"
                />
                <Radio
                  {...family.hasPartner}
                  label="I'm my own partner on this adventure! 🚀"
                  checked={family.hasPartner.value === 'false'}
                  value="false"
                />
              </RadioGroup>
            ) : null}

            <Collapse in={family.hasPartner.value === 'true'}>
              <div>
                <NameFieldset
                  firstName={family.partner.firstName}
                  firstNameLabel="Partner's First Name"
                  lastName={family.partner.lastName}
                  lastNameLabel="Partner's Last Name"
                />
                <PartnerAccessForm
                  registryId={this.props.fields.registry.id.value}
                  firstName={family.partner.firstName.value}
                  lastName={family.partner.lastName.value}
                  onSubmit={this.handlePartnerAccessFormSubmit.bind(this)}
                />
              </div>
            </Collapse>
          </div>
        ) : null}

        <RadioGroup
          title={family.hasPartner.value === 'true' ? 'We are' : 'I am'}
        >
          {map(CONFIG.registry.planningStageOptions, (stage) => {
            return (
              <Radio
                {...family.planningStage}
                key={stage}
                label={stage}
                value={stage}
                checked={family.planningStage.value === stage}
              />
            )
          })}
        </RadioGroup>

        <RadioGroup title={this.expectingChildrenTitle()}>
          <Row>
            {map(family.expectingChildren, (child, idx) => {
              return (
                <Col sm={4} key={idx}>
                  {map(CONFIG.registry.expectingChildrenOptions, (option) => {
                    return (
                      <Radio
                        {...child}
                        key={option}
                        label={option}
                        value={option}
                        checked={child.value === option}
                      />
                    )
                  })}
                  {idx > 0 ? (
                    <Link
                      className="h7 text-uppercase"
                      onClick={() => family.expectingChildren.removeField(idx)}
                    >
                      remove
                    </Link>
                  ) : null}
                  {idx < family.expectingChildren.length - 1 ? (
                    <hr className="visible-xs mvl" />
                  ) : null}
                </Col>
              )
            })}
          </Row>
        </RadioGroup>

        <div className="mvl">
          <AddOne
            onClick={family.expectingChildren.addField}
            message="1+ (I'm expecting multiples)"
          />
        </div>

        <ModalFooter>
          <SaveButton submitting={this.props.submitting} block />
        </ModalFooter>
      </Form>
    )
  }

  handlePartnerAccessFormSubmit() {
    return this.props.saveRegistry(this.props.values, { hideModal: false })
  }

  handleSubmit(data) {
    if (data.registry.family.hasPartner === 'false') {
      // Clear out any partener data if partner is removed
      data.registry.family.partner = {
        firstName: null,
        lastName: null,
        email: null,
      }
    }
    return this.props.saveRegistry(data)
  }

  expectingChildrenTitle() {
    let family = this.props.fields.registry.family
    let pronoun = family.hasPartner.value === 'true' ? "We're" : "I'm"
    let message = `${pronoun} having`
    if (family.expectingChildren.length === 1) message += ' a'
    return message
  }
}

export const familyDetailsValidator = createValidator({
  'user.firstName': [required],
  'user.lastName': [required],
})

const mapStateToProps = (state) => {
  const partner = getPartner(state)
  const hasPartner = getHasPartner(state)
  const planningStage = getPlanningStage(state)
  const expectingChildren = getExpectingChildren(state)
  return {
    initialValues: {
      registry: {
        id: getRegistryId(state),
        family: {
          expectingChildren: expectingChildren.length
            ? expectingChildren
            : [''], // empty string because we don't want to select a value
          hasPartner: hasPartner != null ? hasPartner.toString() : '',
          partner: partner,
          planningStage: planningStage,
        },
      },
      user: state.user,
    },
    userOwnsRegistry: getUserOwnsRegistry(state),
    partnerCollaborator: getPartnerCollaborator(state),
  }
}

export default reduxForm(
  {
    form: 'familyDetails',
    fields: [
      'user.firstName',
      'user.lastName',
      'registry.id',
      'registry.family.hasPartner',
      'registry.family.partner.firstName',
      'registry.family.partner.lastName',
      'registry.family.planningStage',
      'registry.family.expectingChildren[]',
    ],
    validate: familyDetailsValidator,
  },
  mapStateToProps,
  {
    saveRegistry,
  }
)(FamilyDetailsForm)
